'use client';

import { CircleCheck, ShoppingCart } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { cn } from '@/shared/utils/cn';

import type { CourseCardCartState } from '../types/course-card';

interface CourseCardCartIconButtonProps {
  cartState: CourseCardCartState;
  onAddingAnimationComplete?: () => void;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  'aria-label'?: string;
}

const ADDING_DURATION_MS = 700;
const ICON_SWAP_DURATION_MS = 220;
const CHECK_VISIBLE_DURATION_MS = 240;
const CHECK_NOTIFICATION_DELAY_MS = ICON_SWAP_DURATION_MS + CHECK_VISIBLE_DURATION_MS;

const DOTS = [
  { x: -7, y: -14 },
  { x: 0,  y: -17 },
  { x: 7,  y: -14 },
] as const;

export function CourseCardCartIconButton({
  cartState,
  onAddingAnimationComplete,
  onClick,
  className,
  'aria-label': ariaLabel,
}: CourseCardCartIconButtonProps) {
  const [isAdding, setIsAdding] = useState(false);
  const timerRef = useRef<number | null>(null);
  const checkTimerRef = useRef<number | null>(null);
  const shouldNotifyAfterCheckRef = useRef(false);
  const onAddingAnimationCompleteRef = useRef(onAddingAnimationComplete);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    onAddingAnimationCompleteRef.current = onAddingAnimationComplete;
  }, [onAddingAnimationComplete]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
    },
    [],
  );

  const isInCart = cartState === 'flash' || cartState === 'pay';
  // Blue + check only once the dots animation has finished. While adding, the
  // button keeps its primary (purple) look so the color shift reads as a state change.
  const showAddedState = isInCart && !isAdding;

  useEffect(() => {
    if (!showAddedState || !shouldNotifyAfterCheckRef.current) return;

    if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
    checkTimerRef.current = window.setTimeout(() => {
      checkTimerRef.current = null;
      shouldNotifyAfterCheckRef.current = false;
      onAddingAnimationCompleteRef.current?.();
    }, CHECK_NOTIFICATION_DELAY_MS);

    return () => {
      if (checkTimerRef.current) {
        window.clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [showAddedState]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (cartState === 'idle' && !isAdding) {
      setIsAdding(true);
      shouldNotifyAfterCheckRef.current = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
      timerRef.current = window.setTimeout(() => {
        setIsAdding(false);
        timerRef.current = null;
      }, ADDING_DURATION_MS);
    }
    onClick?.(event);
  };

  return (
    <motion.button
      aria-label={ariaLabel}
      className={cn(
        'focus-visible:ring-(--color-primary-500) relative isolate inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition-[background-color,border-color,color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none',
        showAddedState
          ? 'border-(--brand-secondary) bg-(--brand-secondary) text-adipa-primary-foreground hover:bg-[color-mix(in_srgb,var(--brand-secondary)_88%,var(--brand-navy))] hover:shadow-[0_8px_18px_rgba(44,183,255,0.22)]'
          : 'border-(--color-primary-500) bg-(--color-primary-500) text-adipa-primary-foreground hover:bg-(--color-primary-600) hover:border-(--color-primary-600) hover:shadow-[0_8px_18px_rgba(112,78,253,0.22)]',
        className,
      )}
      type="button"
      whileTap={shouldReduceMotion ? undefined : { scale: 0.91 }}
      onClick={handleClick}
    >
      <div className="relative flex h-6 items-center justify-center">
        {!shouldReduceMotion && (
          <AnimatePresence>
            {isAdding ? (
              <>
                {DOTS.map((dot, i) => (
                  <motion.span
                    key={i}
                    className="absolute h-1.5 w-1.5 rounded-full bg-white"
                    animate={{
                      x: [dot.x, 0],
                      y: [dot.y, 3],
                      opacity: [0, 1, 1, 0],
                      scale: [0, 0.9, 0.9, 0],
                    }}
                    exit={{ opacity: 0, scale: 0 }}
                    initial={{ x: dot.x, y: dot.y, opacity: 0, scale: 0 }}
                    transition={{
                      duration: 0.46,
                      delay: i * 0.1,
                      ease: [0.4, 0, 0.6, 1],
                      times: [0, 0.3, 0.75, 1],
                    }}
                  />
                ))}
              </>
            ) : null}
          </AnimatePresence>
        )}

        <motion.div
          animate={
            isAdding && !shouldReduceMotion
              ? { y: [0, -2, 1, 0] }
              : { y: 0 }
          }
          className="flex items-center justify-center"
          transition={{ duration: 0.3, delay: 0.44, ease: 'easeOut' }}
        >
          <AnimatePresence initial={false} mode="wait">
            {showAddedState ? (
              <motion.span
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                initial={{ scale: 0.4, opacity: 0 }}
                key="check"
                transition={{ type: 'spring', stiffness: 420, damping: 18 }}
              >
                <CircleCheck aria-hidden="true" className="h-5 w-5" strokeWidth={2.2} />
              </motion.span>
            ) : (
              <motion.span
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                initial={{ scale: 0.4, opacity: 0 }}
                key="cart"
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ShoppingCart aria-hidden="true" className="h-5 w-5" strokeWidth={2} />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.button>
  );
}
